import { env } from "../env";
import { ApolloClient, ApolloLink, InMemoryCache } from "@apollo/client";
import { setContext } from "@apollo/client/link/context";
import { onError } from "@apollo/client/link/error";
import { Observable } from "@apollo/client/utilities";
import * as Sentry from "@sentry/react";
import { createUploadLink } from "apollo-upload-client";
import { getSdk } from "generated.sdk";
import { GraphQLClient } from "graphql-request";
import { sign, verify } from "jsonwebtoken";
import jwtDecode from "jwt-decode";

const REFRESH_TOKEN_MUTATION = `
  mutation RefreshToken {
    refreshToken(revokeRefreshToken: true) {
      success
      errors
      token {
        token
      }
    }
  }
`;

const REVOKE_TOKEN_MUTATION = `
  mutation RevokeToken {
    revokeToken {
      success
    }
  }
`;

let refreshTokenRequest = null;

/** The access token's expiry, in milliseconds since the epoch.
 *
 * The API (django-gqlauth) does not use the standard numeric `exp` claim. It
 * nests its own JSON in a `payload` claim and writes `exp` with its
 * JWT_TIME_FORMAT, "%Y-%m-%d-%H-%M-%S-%z": "2026-10-13-08-30-30-+0000". No
 * browser's Date parses that, so every token used to read as expired and the
 * app refreshed before nearly every request, rotating the refresh token each
 * time.
 *
 * Any other format falls through to Date parsing. If the API's format ever
 * changes, that yields NaN and the token reads as expired: the app refreshes
 * early rather than trusting a token it cannot date.
 */
const GQLAUTH_TIME =
  /^(\d{4})-(\d{2})-(\d{2})-(\d{2})-(\d{2})-(\d{2})-([+-])(\d{2})(\d{2})$/;

const parseTokenExpiry = (exp) => {
  const match = typeof exp === "string" ? GQLAUTH_TIME.exec(exp) : null;
  if (!match) {
    return new Date(exp).getTime();
  }
  const [, year, month, day, hour, minute, second, sign, offH, offM] = match;
  const offsetMs =
    (sign === "-" ? -1 : 1) * (Number(offH) * 60 + Number(offM)) * 60000;
  return (
    Date.UTC(
      Number(year),
      Number(month) - 1,
      Number(day),
      Number(hour),
      Number(minute),
      Number(second)
    ) - offsetMs
  );
};

const isValidToken = (accessToken) => {
  if (!accessToken) {
    return false;
  }
  try {
    const decoded = JSON.parse(jwtDecode(accessToken).payload);
    return parseTokenExpiry(decoded.exp) > Date.now();
  } catch (err) {
    return false;
  }
};

const getStoredAccessToken = () => {
  try {
    return localStorage.getItem("accessToken");
  } catch (err) {
    return null;
  }
};

const setSession = (accessToken) => {
  try {
    if (accessToken) {
      localStorage.setItem("accessToken", accessToken);
    } else {
      localStorage.removeItem("accessToken");
    }
  } catch (err) {
    // localStorage can be unavailable after jsdom teardown or in privacy-restricted browsers.
  }
};

const redirectToSignIn = () => {
  const currentPath = `${window.location.pathname}${window.location.search}`;
  const signInPath = "/auth/sign-in";
  if (window.location.pathname === signInPath) {
    return;
  }
  window.location.assign(
    `${signInPath}?returnTo=${encodeURIComponent(currentPath)}`
  );
};

const normalizeGraphQLErrors = (graphQLErrors) => {
  if (!graphQLErrors) {
    return [];
  }

  if (Array.isArray(graphQLErrors)) {
    return graphQLErrors;
  }

  if (Array.isArray(graphQLErrors.errors)) {
    return graphQLErrors.errors;
  }

  return [graphQLErrors];
};

const isAuthError = (error) => {
  const graphQLErrors = normalizeGraphQLErrors(
    error?.graphQLErrors || error?.response?.errors
  );
  const networkError = error?.networkError || error;
  const statusCode = networkError?.statusCode || networkError?.status;

  if (statusCode === 401) {
    return true;
  }

  return graphQLErrors.some((graphQLError) => {
    const code = graphQLError?.extensions?.code || graphQLError?.code;
    const message = (graphQLError?.message || "").toLowerCase();
    return (
      code === "invalid_token" ||
      code === "expired_token" ||
      code === "not_authenticated" ||
      message.includes("invalid token") ||
      message.includes("expired token") ||
      message.includes("not authenticated") ||
      message.includes("authentication credentials")
    );
  });
};

const getOperationName = (operation) => operation?.operationName || "unknown";

const reportApolloError = ({ graphQLErrors, networkError, operation }) => {
  const normalizedGraphQLErrors = normalizeGraphQLErrors(graphQLErrors);

  if (isAuthError({ graphQLErrors, networkError })) {
    return;
  }

  const operationName = getOperationName(operation);

  normalizedGraphQLErrors.forEach((graphQLError) => {
    const message = graphQLError?.message || "GraphQL error";
    const error = new Error(`GraphQL error in ${operationName}: ${message}`);
    error.name = "GraphQLError";

    Sentry.withScope((scope) => {
      scope.setTag("graphql.operation", operationName);
      scope.setContext("graphql", {
        operationName,
        path: graphQLError?.path,
        locations: graphQLError?.locations,
        extensions: graphQLError?.extensions,
      });
      Sentry.captureException(error);
    });
  });

  if (networkError) {
    const error =
      networkError instanceof Error
        ? networkError
        : new Error(networkError?.message || "GraphQL network error");

    Sentry.withScope((scope) => {
      scope.setTag("graphql.operation", operationName);
      scope.setContext("graphql", {
        operationName,
        statusCode: networkError?.statusCode || networkError?.status,
      });
      Sentry.captureException(error);
    });
  }
};

/** How long an auth request may take before it counts as failed.
 *
 * A refresh runs under a lock that every tab waits on, so a request hanging
 * on a stalled connection would stall every tab with it. The deadline turns a
 * hang into a failure, and a failure that is not the API's answer keeps the
 * session (see `authRejected`).
 */
const AUTH_REQUEST_TIMEOUT_MS = 15000;

const executeAuthMutation = async (query) => {
  const token = getStoredAccessToken();
  // A controller and a timer rather than AbortSignal.timeout(): Safari has
  // Web Locks from 15.4 but AbortSignal.timeout only from 16.
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), AUTH_REQUEST_TIMEOUT_MS);
  try {
    const response = await fetch(env.REACT_APP_GRAPHQL_API_URL, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `JWT ${token}` } : {}),
      },
      body: JSON.stringify({ query }),
      signal: controller.signal,
    });

    if (!response.ok) {
      throw new Error(`Auth request failed with status ${response.status}`);
    }

    const payload = await response.json();
    if (payload.errors?.length) {
      throw new Error(payload.errors[0].message || "Auth request failed");
    }
    return payload.data;
  } finally {
    clearTimeout(timeout);
  }
};

/** The API answered the refresh and said this session is over.
 *
 * Only this ends the session. Every other failure - no connection, the
 * deadline above, a 502 during a deploy - says nothing about the refresh
 * cookie, which is still there and still good. Signing out on those turned a
 * laptop waking before its Wi-Fi into a lost session. Marked with a property
 * rather than an Error subclass, because transpiled subclasses of built-ins
 * can fail `instanceof`, and missing a real rejection would leave the user
 * stuck instead of at the sign-in page.
 */
const authRejected = (message) =>
  Object.assign(new Error(message), {
    name: "AuthRejectedError",
    authRejected: true,
  });

const isAuthRejected = (err) => err?.authRejected === true;

const REFRESH_LOCK_NAME = "podium-token-refresh";

/** Runs `refresh` while holding a lock shared by every tab on this origin.
 *
 * Each refresh revokes the refresh token in the cookie and sets a new one.
 * Tabs share that cookie, and they share the access token in localStorage,
 * so it expires in every tab at the same moment and each polling tab then
 * refreshes. Two refreshes sent with the same cookie cannot both succeed: the
 * second presents the token the first just revoked, fails, and signs the user
 * out. The in-tab `refreshTokenRequest` only merges calls within one tab;
 * this makes the tabs take turns.
 *
 * Without the Web Locks API (old browsers, jsdom) it runs unguarded, as it
 * did before.
 */
const withRefreshLock = (refresh) =>
  typeof navigator !== "undefined" && navigator.locks?.request
    ? navigator.locks.request(REFRESH_LOCK_NAME, refresh)
    : refresh();

const getRefreshTokenRequest = () => {
  if (!refreshTokenRequest) {
    const tokenBeforeWaiting = getStoredAccessToken();
    refreshTokenRequest = withRefreshLock(() => {
      // Another tab may have refreshed while this one waited for the lock. A
      // different, unexpired token means it did, and refreshing again would
      // only rotate the cookie for nothing. An unchanged token means nobody
      // did, which also covers a caller forcing a refresh because the API
      // rejected a token that still looks valid here.
      const current = getStoredAccessToken();
      if (current && current !== tokenBeforeWaiting && isValidToken(current)) {
        return current;
      }

      // Stored before the lock is released, so the next tab in line sees it.
      return executeAuthMutation(REFRESH_TOKEN_MUTATION).then((data) => {
        const refreshToken = data?.refreshToken;
        const accessToken = refreshToken?.token?.token;
        if (!refreshToken?.success || !accessToken) {
          throw authRejected("Token refresh was rejected");
        }
        setSession(accessToken);
        return accessToken;
      });
    })
      .catch((err) => {
        // Keep the stored token on anything but a rejection: the next request
        // retries the refresh with the same, still-valid cookie.
        if (isAuthRejected(err)) {
          setSession(null);
        }
        throw err;
      })
      .finally(() => {
        refreshTokenRequest = null;
      });
  }
  return refreshTokenRequest;
};

const refreshAccessToken = async ({ redirectOnFailure = true } = {}) => {
  try {
    return await getRefreshTokenRequest();
  } catch (err) {
    // A failed connection fails this request the ordinary way; only a
    // rejection sends the user to sign in.
    if (redirectOnFailure && isAuthRejected(err)) {
      redirectToSignIn();
    }
    throw err;
  }
};

const revokeRefreshToken = async () => {
  try {
    await executeAuthMutation(REVOKE_TOKEN_MUTATION);
  } finally {
    setSession(null);
  }
};

const httpLink = createUploadLink({
  uri: env.REACT_APP_GRAPHQL_API_URL,
  credentials: "include",
});

const authLink = setContext(async (_, { headers }) => {
  let token = getStoredAccessToken();
  if (token && !isValidToken(token)) {
    token = await refreshAccessToken().catch(() => null);
  }

  return {
    headers: {
      ...headers,
      ...(token ? { Authorization: `JWT ${token}` } : {}),
    },
  };
});

const authErrorLink = onError(
  ({ graphQLErrors, networkError, operation, forward }) => {
    if (
      !isAuthError({ graphQLErrors, networkError }) ||
      operation.getContext().authRetry
    ) {
      return undefined;
    }

    return new Observable((observer) => {
      let subscriber;
      refreshAccessToken()
        .then((token) => {
          operation.setContext(({ headers = {} }) => ({
            authRetry: true,
            headers: {
              ...headers,
              Authorization: `JWT ${token}`,
            },
          }));

          subscriber = forward(operation).subscribe({
            next: observer.next.bind(observer),
            error: observer.error.bind(observer),
            complete: observer.complete.bind(observer),
          });
        })
        .catch((err) => {
          observer.error(err);
        });

      return () => {
        subscriber?.unsubscribe();
      };
    });
  }
);

const sentryErrorLink = onError(reportApolloError);

const userClient = new ApolloClient({
  link: ApolloLink.from([sentryErrorLink, authErrorLink, authLink, httpLink]),
  cache: new InMemoryCache(),
});

const client = new GraphQLClient(process.env.REACT_APP_GRAPHQL_API_URL, {
  credentials: "include",
});

async function withJwtToken(action) {
  let token = getStoredAccessToken();
  if (token && !isValidToken(token)) {
    token = await refreshAccessToken().catch(() => null);
  }

  client.setHeaders({
    Authorization: token ? `JWT ${token}` : "",
    "Content-Type": "application/json",
  });

  try {
    return await action();
  } catch (err) {
    if (!isAuthError(err)) {
      throw err;
    }

    const refreshedToken = await refreshAccessToken();
    client.setHeaders({
      Authorization: `JWT ${refreshedToken}`,
      "Content-Type": "application/json",
    });
    return await action();
  }
}

const sdkClient = getSdk(client, withJwtToken);

export {
  verify,
  sign,
  isValidToken,
  isAuthRejected,
  setSession,
  refreshAccessToken,
  revokeRefreshToken,
  reportApolloError,
  userClient,
  sdkClient,
};
